import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
    StyledPaginator,
    StyledPaginatorActive,
    StyledSubPageHeader,
    StyledBrandLink,
    StyledPagination,
    StyledPaginatorNavButton,
    StyledPaginatorNavIcon,
    StyledPaginatorCarousel,
} from './styled';
import { Link } from '~/shared/components';
import { Carousel } from '~/shared/components/Carousel';
import { Container } from '~/shared/components/Container';
import Grid from '~/shared/components/Grid/Grid';
import Spacer from '~/theme/design-system/components/Spacer';
import { Brands } from '../product-details-page/pdp.definitions';

type Props = {
    brands?: Brands | null;
};

export const BrandOverviewPage = (props: Props) => {
    const { push } = useRouter();
    const [selectedPage, setSelectedPage] = useState<string>('');

    const paginationNextRef = useRef<HTMLButtonElement | null>(null);
    const paginationPrevRef = useRef<HTMLButtonElement | null>(null);

    if (!props.brands) {
        return <></>;
    }

    const brands: Record<string, Brands> = {};
    Object.keys(props.brands).map(function (key: string) {
        brands[key.charAt(0).toUpperCase() + key.slice(1)] = props.brands?.[key];
    });

    const handleGoToBrands = (subpage: string) => {
        push('#' + subpage);
    };

    const pages: string[] = [];
    const sorted: Brands = Object.keys(brands)
        .sort()
        .reduce((accumulator, key: string) => {
            const page = isNaN(key.charAt(0) as unknown as number) ? key.charAt(0) : '0-9';
            if (!pages.includes(page)) {
                pages.push(page);
            }
            // eslint-disable-next-line no-prototype-builtins
            if (!accumulator.hasOwnProperty(page)) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                accumulator[page] = [];
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            accumulator[page].push({ title: key, url: brands[key] });
            return accumulator;
        }, {});

    const getPageNameQuerySafe = (name: string) => {
        return name
            .replace('-', '_')
            .replace('æ', 'ae')
            .replace('Æ', 'AE')
            .replace('ø', 'oe')
            .replace('Ø', 'OE')
            .replace('å', 'aa')
            .replace('Å', 'AA');
    };

    const paginator = pages.map(function (page) {
        const pageQuerySafe = getPageNameQuerySafe(page);
        return pageQuerySafe == selectedPage ? (
            <StyledPaginatorActive>
                <span>{page}</span>
            </StyledPaginatorActive>
        ) : (
            <StyledPaginator
                onClick={() => {
                    setSelectedPage(pageQuerySafe);
                    handleGoToBrands(pageQuerySafe);
                }}
            >
                <span>{page}</span>
            </StyledPaginator>
        );
    });

    const renderBrandLinks = (brandSubList: Brands) => {
        const numBrands = brandSubList.length;
        const brandsPerColumn = Math.ceil(numBrands / 3);
        const linkColumn1 = [];
        const linkColumn2 = [];
        const linkColumn3 = [];
        for (let i = 0; i < numBrands; i++) {
            if (i < brandsPerColumn) {
                linkColumn1.push(
                    <li style={{ padding: '5px 0' }}>
                        <Link href={brandSubList[i].url}>
                            <StyledBrandLink>{brandSubList[i].title}</StyledBrandLink>
                        </Link>
                    </li>,
                );
            } else if (brandsPerColumn <= i && i < 2 * brandsPerColumn) {
                linkColumn2.push(
                    <li style={{ padding: '5px 0' }}>
                        <Link href={brandSubList[i].url}>
                            <StyledBrandLink>{brandSubList[i].title}</StyledBrandLink>
                        </Link>
                    </li>,
                );
            } else {
                linkColumn3.push(
                    <li style={{ padding: '5px 0' }}>
                        <Link href={brandSubList[i].url}>
                            <StyledBrandLink>{brandSubList[i].title}</StyledBrandLink>
                        </Link>
                    </li>,
                );
            }
        }
        return [linkColumn1, linkColumn2, linkColumn3];
    };

    return (
        <Container hasVerticalGutter hasGutter>
            <StyledPagination>
                <StyledPaginatorNavButton position="left" ref={paginationPrevRef}>
                    <StyledPaginatorNavIcon />
                </StyledPaginatorNavButton>

                <StyledPaginatorCarousel>
                    <Carousel
                        items={paginator}
                        nextRef={paginationNextRef}
                        prevRef={paginationPrevRef}
                    />
                </StyledPaginatorCarousel>

                <StyledPaginatorNavButton position="right" ref={paginationNextRef}>
                    <StyledPaginatorNavIcon />
                </StyledPaginatorNavButton>
            </StyledPagination>
            <Grid flow="row" columnGap="large">
                <Spacer space="80px" />
                {Object.keys(sorted).map((key) => {
                    const brandLinks = renderBrandLinks(sorted[key]);
                    const pageQuerySafe = getPageNameQuerySafe(key);

                    return (
                        <Grid flow={'row'}>
                            <Grid flow={'row'}>
                                <StyledSubPageHeader id={pageQuerySafe}>{key}</StyledSubPageHeader>
                            </Grid>
                            <Grid flow="column" columns="1fr 1fr 1fr" columnGap="extrasmall">
                                <Grid flow="column" style={{ zIndex: '10' }}>
                                    <ul>{brandLinks[0]}</ul>
                                </Grid>
                                <Grid flow="column" style={{ zIndex: '10' }}>
                                    <ul>{brandLinks[1]}</ul>
                                </Grid>
                                <Grid flow="column" style={{ zIndex: '10' }}>
                                    <ul>{brandLinks[2]}</ul>
                                </Grid>
                            </Grid>
                            <Spacer space="40px" />
                        </Grid>
                    );
                })}
                <Spacer space="100px" />
            </Grid>
        </Container>
    );
};
